/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/styles';
import { isArray } from 'lodash';
import ProcessHandler from '@src/components/ProcessHandler';
import {
  DURATION,
  MERGE_PRODUCT_DURATION,
  PACK_DURATION,
  PACKAGE_DURATION_LABEL,
  PACKAGE_EXPIRED,
  PACKAGE_LEVEL_V3,
  PACKAGE_TYPE,
} from '@src/constants/package';
import api from '@src/apis';
import {
  filterPackagesV3,
  checkHasDiscountPackage,
  getPackageCurrentPrice,
  getTotalPricePackage,
  calculateSaleOffMonthlyPrice,
  roundSaleOffPercentage,
  checkFreePackage,
} from '@src/services/package';
import { EVENT_TYPE } from '@src/constants';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import moment from 'moment';
import {
  checkVoucherByProduct,
  getSavedPriceBasePrice,
  verifyPolicy,
  verifyTime,
} from '@src/services/voucher';
import { VOUCHER_TYPE } from '@src/constants/voucher';
import { PAYMENT_HUB_RENEWAL_TYPE } from '@src/constants/subscription';
import { checkVietNam } from '@src/utils/checkCountry';
import { StyledPackages } from './index.style';
import NewPackageItem from './NewPackageItem';
import SwiperVoucherBanner from './SwiperVoucherBanner';
import CustomPackage from './CustomPackage';
import FreePackageRow from './FreePackageRow';

const NUMBER_ELEMENTS_SHOW_CUSTOM_PACK_ROW = 4;

const NewPricingModelV3 = () => {
  const theme = useTheme();
  const isVietNam = checkVietNam();
  const location = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);
  const [numberOfElements, setNumberOfElements] = useState(0);
  const { openSidebar } = useSelector((state) => state.layout);

  const getPackageDuration = () => {
    const packageDurationParam = queryParams.get('duration') || DURATION.YEARLY;
    return Object.values(DURATION).includes(packageDurationParam)
      ? packageDurationParam
      : DURATION.YEARLY;
  };

  const initialPackageDuration = getPackageDuration();

  const packageType = PACKAGE_TYPE.STUDIO;

  const [packageDuration, setPackageDuration] = useState(
    initialPackageDuration,
  );

  const [packages, setPackages] = useState([]);
  const [packagesDraft, setPackagesDraft] = useState([]);
  const [totalVoucher, setTotalVoucher] = useState([]);
  const [currentPackages, setCurrentPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasDiscountPackage, setHasDiscountPackage] = useState(false);
  const [isFirstTimePaid, setIsFirstTimePaid] = useState(false);
  const [firstPaidVoucher, setFirstPaidVoucher] = useState(null);
  const [voucherBannerToShow, setVoucherBannerToShow] = useState([]);
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const packagesRef = useRef();
  const { usingPackage } = useSelector((state) => state.user);
  const { bannerVoucher } = useSelector((state) => state.voucher);
  const { user } = useSelector((state) => state.auth);
  const { getFeatureValue } = useFeatureFlags();
  const promotedMoePackageCode = getFeatureValue(
    FEATURE_KEYS.PROMOTED_PACKAGE_MOE,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const discountForFirstPaidUser = getFeatureValue(
    FEATURE_KEYS.DISCOUNT_FOR_FIRST_PAID_USER,
  );

  const isPushEventViewPricing = getFeatureValue(
    FEATURE_KEYS.PUSH_EVENT_VIEW_PRICING,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );
  const useRenameMergeProduct = getFeatureValue(
    FEATURE_KEYS.RENAME_MERGE_PRODUCT,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSetCurrentPackages = (duration, allPackages) => {
    const displayPackages = filterPackagesV3({
      duration,
      allPackages,
      packageType,
    });

    // Sort packages free put to the end of list
    setCurrentPackages(
      displayPackages?.sort((a, b) =>
        a.rank === 1 ? 1 : b.rank === 1 ? -1 : a.rank - b.rank,
      ),
    );
    const isHasDiscountPackage = checkHasDiscountPackage(displayPackages);
    setHasDiscountPackage(isHasDiscountPackage);
  };

  const handleDiscountEnd = () => {
    const isHasDiscountPackage = checkHasDiscountPackage(currentPackages);
    setHasDiscountPackage(isHasDiscountPackage);
  };

  const handleChangePackageDuration = (value) => {
    setPackages(packagesDraft);
    setPackageDuration(value);
    queryParams.set('duration', value);
    history.replace({ search: queryParams.toString() });
  };

  // Lấy tất cả các gói cước
  const fetchPackages = async () => {
    setLoading(true);
    const pkgQuery = { type: packageType, active: true, purchasable: true };
    if (useRenameMergeProduct && packageType === PACKAGE_TYPE.DUBBING) {
      pkgQuery.type = PACKAGE_TYPE.STUDIO;
    }
    const { status, result } = await api.packages.getPackages(pkgQuery);
    setLoading(false);
    if (status) {
      setPackages(result.packages);
      setPackagesDraft(result.packages);
    }
  };

  const fetchVoucherForFirstPaidUser = async () => {
    const response = await api.vouchers.getVoucherForFirstPaidOrder();
    if (response.status) {
      const voucher = response.result?.[0];
      const satisfiedTime = verifyTime(voucher);
      if (satisfiedTime) setFirstPaidVoucher(voucher);
    }
  };

  const fetchTotalVouchers = async () => {
    const data = await api.vouchers.getVouchers();
    if (data.status) {
      setTotalVoucher(data.result);
    }
  };

  const filterUseableVoucher = (currPackageId) =>
    totalVoucher
      .filter((voucher) => {
        const satisfiedTime = verifyTime(voucher);
        const satisfiedPolicy = verifyPolicy(voucher, currPackageId);

        const isPriceOrPercentVoucher =
          voucher.type === VOUCHER_TYPE.PERCENT ||
          voucher.type === VOUCHER_TYPE.PRICE;

        const isVoucherOnlyForAutoRenew =
          voucher?.policy?.renewalType === PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW;

        const canUse =
          satisfiedTime &&
          satisfiedPolicy &&
          voucher.remainingAmount &&
          !voucher.isPrivate &&
          voucher.usedAmount < voucher.numReuse &&
          isPriceOrPercentVoucher &&
          !isVoucherOnlyForAutoRenew;

        return canUse;
      })
      .map((voucher) => ({
        ...voucher,
        satisfiedTime: verifyTime(voucher),
        satisfiedPolicy: verifyPolicy(voucher, currPackageId),
        canUse: true,
      }))
      .sort((a) => (a.canUse ? -1 : 1));

  // Lấy % giảm giá tối đa theo năm

  const getCorrespondMonthPkg = ({ packageName, level, expiresIn }) => {
    if (expiresIn === PACKAGE_EXPIRED.MONTHLY) {
      return packages.find(
        (pkg) =>
          pkg.name[language] === packageName &&
          pkg.level === level &&
          pkg?.expiresIn === PACK_DURATION.MONTH,
      );
    }

    return packages.find(
      (pkg) =>
        pkg.name[language] === packageName &&
        pkg.level === level &&
        pkg?.expiresIn !== expiresIn &&
        pkg?.expiresIn === PACK_DURATION.MONTH,
    );
  };

  const getBestVoucherAndPackagePrice = (pkg) => {
    const canUseVouchers = filterUseableVoucher(pkg?.id);
    const { startAt, endAt } = pkg.discountConfig || {};
    const isDiscountPackage = startAt && moment().isBetween(startAt, endAt);
    const { price, usdPrice } = getPackageCurrentPrice(pkg, true);
    const originalPrice = isVietNam ? price : usdPrice;

    const listVoucherSortByRanking = canUseVouchers
      .map((voucherItem) => {
        const reducedPrice = getSavedPriceBasePrice({
          voucher: voucherItem,
          currPrice: isDiscountPackage
            ? pkg?.discountConfig?.price || price
            : price,
          currUsdPrice: isDiscountPackage
            ? pkg?.discountConfig?.usdPrice || usdPrice
            : usdPrice,
        });
        return { ...voucherItem, reducedPrice };
      })
      .sort((a, b) => b.reducedPrice - a.reducedPrice);

    const bestVoucher = listVoucherSortByRanking.length
      ? listVoucherSortByRanking[0]
      : null;

    return {
      totalPrice: getTotalPricePackage(
        pkg,
        isDiscountPackage,
        bestVoucher,
        isVietNam,
      ),
      bestVoucher,
      isDiscountPackage,
      originalPrice,
    };
  };

  const saveUpToPercent = () => {
    const packagesYear = packages?.flatMap((pkg) => {
      if (
        packageType === PACKAGE_TYPE.API &&
        pkg.expiresIn === PACKAGE_EXPIRED.YEARLY
      )
        return pkg;
      return pkg.expiresIn === PACK_DURATION.YEAR ? pkg : [];
    });

    const percents = packagesYear?.map((pkg) => {
      const totalPrice = isVietNam ? pkg?.price : pkg.usdPrice;

      const correspondMonthPkg = getCorrespondMonthPkg({
        packageName: pkg?.name?.[language],
        level: pkg.level,
        expiresIn: pkg.expiresIn,
      });
      const correspondMonthPrice = isVietNam
        ? correspondMonthPkg?.price
        : correspondMonthPkg?.usdPrice;

      if (!correspondMonthPrice || !totalPrice) return 0;

      const monthByDuration = Math.round(pkg?.expiresIn / 30);
      const saleOffPercentage =
        (1 - totalPrice / (correspondMonthPrice * monthByDuration)) * 100;
      return saleOffPercentage;
    });

    return roundSaleOffPercentage(
      percents.length > 0 ? Math.max(...percents) : 0,
    );
  };

  const checkShowBannerVoucherNew = () => {
    if (!bannerVoucher || !bannerVoucher.length) return false;
    const result = bannerVoucher.filter((voucher) => {
      const { endTime } = voucher;
      const isValidTime = endTime && moment().isBefore(endTime);
      if (!isValidTime) return false;

      const { applyAllPackages, packages: validPackages } =
        voucher?.policy || {};
      if (applyAllPackages) return true;

      return validPackages.some(
        (pkg) => pkg.type.toLowerCase() === packageType.toLowerCase(),
      );
    });

    return result;
  };

  const checkExpiredDate = (expiryDate) => {
    if (!expiryDate) return false;
    return moment().isAfter(expiryDate);
  };

  const getUsingPackage = (pkgType) => {
    switch (pkgType) {
      case PACKAGE_TYPE.STUDIO: {
        const studioPackage = packages.find(
          (pkg) => pkg.code === user?.packageCode,
        );
        return {
          ...studioPackage,
          isExpires: checkExpiredDate(user?.packageExpiryDate),
        };
      }
      case PACKAGE_TYPE.API: {
        const apiPackage = packages.find(
          (pkg) => pkg.code === user?.apiPackage?.packageCode,
        );
        return {
          ...apiPackage,
          isExpires: checkExpiredDate(user?.apiPackage?.packageExpiryDate),
        };
      }
      default:
        return null;
    }
  };

  const getPrice = (pkg) => {
    // Calculate sale off percentage
    const { totalPrice, bestVoucher, isDiscountPackage, originalPrice } =
      getBestVoucherAndPackagePrice(pkg);

    if (!originalPrice || !totalPrice) return 0;

    const saleOffPercentage = (1 - totalPrice / originalPrice) * 100;
    const saleOffPercentageRounded = roundSaleOffPercentage(saleOffPercentage);

    // Calculate total price and monthly price with sale off
    const monthlyPriceWithSaleOff = calculateSaleOffMonthlyPrice(
      pkg,
      isDiscountPackage,
      bestVoucher,
      isVietNam,
      packageDuration,
    );

    return {
      totalPrice,
      monthlyPriceWithSaleOff,
      saleOffPercentageRounded,
    };
  };
  const isShowRowPackageFree = useMemo(
    () => numberOfElements >= NUMBER_ELEMENTS_SHOW_CUSTOM_PACK_ROW,
    [numberOfElements],
  );

  const renderNormalPackageItem = (pkg) => {
    const correspondMonthPkg = getCorrespondMonthPkg({
      packageName: pkg?.name?.[language],
      level: pkg.level,
      expiresIn: pkg.expiresIn,
    });
    const correspondMonthPrice = isVietNam
      ? correspondMonthPkg?.price
      : correspondMonthPkg?.usdPrice;

    const { totalPrice, monthlyPriceWithSaleOff, saleOffPercentageRounded } =
      getPrice(pkg);

    const packageTopUp =
      packages.find((item) => item.code === pkg.topUpPackageCode) || {};

    const isFreePackage = checkFreePackage(pkg?.code);

    if (
      isFreePackage &&
      isShowRowPackageFree &&
      packageDuration === DURATION.YEARLY
    ) {
      return <FreePackageRow pkg={pkg} duration={packageDuration} />;
    }
    return (
      <NewPackageItem
        packages={packages}
        saleOffPercentage={saleOffPercentageRounded}
        monthlyPriceWithSaleOff={monthlyPriceWithSaleOff || 0}
        correspondMonthPrice={correspondMonthPrice}
        totalPrice={totalPrice}
        isPushEventViewPricing={isPushEventViewPricing}
        pkg={pkg}
        isMobile={isMobile}
        duration={packageDuration}
        hasDiscountPackage={hasDiscountPackage}
        onDiscountEnd={handleDiscountEnd}
        isFirstTimePaid={isFirstTimePaid}
        discountForFirstPaidUser={discountForFirstPaidUser}
        firstPaidVoucher={firstPaidVoucher}
        canUseVouchers={filterUseableVoucher(pkg?.id)}
        currentUsingPackage={getUsingPackage(pkg?.type)}
        packageTopUp={packageTopUp}
        setPackages={setPackages}
        correspondMonthPkg={correspondMonthPkg}
      />
    );
  };
  const renderCustomPackageItem = (pkg) => (
    <CustomPackage
      packages={packages}
      isPushEventViewPricing={isPushEventViewPricing}
      pkg={pkg}
      isMobile={isMobile}
      duration={packageDuration}
      hasDiscountPackage={hasDiscountPackage}
      firstPaidVoucher={firstPaidVoucher}
      totalVoucher={filterUseableVoucher(pkg?.id)}
      currentUsingPackage={getUsingPackage(pkg?.type)}
    />
  );

  const renderPackages = (pkgs = []) => {
    const normalPackage = (pkg) => renderNormalPackageItem(pkg);
    const customPackage = (pkg) => renderCustomPackageItem(pkg);

    return (
      <div className="packages">
        {pkgs?.map((pkg) => (
          <React.Fragment key={`${pkg?.code}`}>
            {pkg?.level.includes(PACKAGE_LEVEL_V3.CUSTOM)
              ? customPackage(pkg)
              : normalPackage(pkg)}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const checkUserFirstTimePaid = async () => {
    const payFees = 'pay_fees';
    const response = await api.orders.getUserPaidOrders(payFees);
    if (response.status && response.result?.orders.length < 1)
      if (discountForFirstPaidUser) setIsFirstTimePaid(true);
  };

  const filterVoucherBannerByProduct = () => {
    const result =
      isArray(bannerVoucher) &&
      bannerVoucher.length > 0 &&
      bannerVoucher.filter((voucher) => {
        const check = checkVoucherByProduct({ voucher, product: packageType });
        return check;
      });

    return result;
  };

  useEffect(() => {
    checkUserFirstTimePaid();
  }, [discountForFirstPaidUser]);

  useEffect(() => {
    fetchVoucherForFirstPaidUser();
    fetchTotalVouchers();
    return () => {
      setFirstPaidVoucher(null);
      setTotalVoucher([]);
    };
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [packageType, useRenameMergeProduct]);

  useEffect(() => {
    const result = filterVoucherBannerByProduct();
    setVoucherBannerToShow(result);
  }, [bannerVoucher, packageType]);

  useEffect(() => {
    handleSetCurrentPackages(packageDuration, packages);
  }, [usingPackage, packageDuration, packages]);

  useEffect(() => {
    if (!promotedMoePackageCode || !packages || !totalVoucher.length) return;

    const promotedPackage = packages.find(
      (pkg) => pkg.code === promotedMoePackageCode,
    );
    if (!promotedPackage) return;

    const validVouchers = filterUseableVoucher(promotedPackage?.id);
    const { price, usdPrice } = promotedPackage;

    let isDiscountPackage = false;
    const { startAt, endAt } = promotedPackage.discountConfig || {};
    const inDiscountTime = startAt && moment().isBetween(startAt, endAt);

    if (inDiscountTime) isDiscountPackage = true;
    else isDiscountPackage = false;

    const listVoucherSortByRanking = validVouchers
      .map((voucherItem) => {
        const reducedPrice = getSavedPriceBasePrice({
          voucher: voucherItem,
          currPrice: isDiscountPackage
            ? promotedPackage?.discountConfig?.price || price
            : price,
          currUsdPrice: isDiscountPackage
            ? promotedPackage?.discountConfig?.usdPrice || usdPrice
            : usdPrice,
        });
        return { ...voucherItem, reducedPrice };
      })
      .sort((a, b) => b.reducedPrice - a.reducedPrice);

    let bestPromotedVoucher;
    if (listVoucherSortByRanking.length > 0)
      [bestPromotedVoucher] = listVoucherSortByRanking;

    const plan = promotedPackage?.code?.split('-')[1];

    window.dataLayer.push({
      event: EVENT_TYPE.VIEW_PRICING,
      userId: user?.id,
      email: user.email,
      product: packageType,
      plan,
      period: packageDuration,
      promoted_package_price: promotedMoePackageCode?.price,
      promoted_package_code: promotedMoePackageCode,
      promoted_package_voucher_code: bestPromotedVoucher?.code,
      promoted_package_voucher_type: bestPromotedVoucher?.type,
      promoted_package_voucher_amount: bestPromotedVoucher?.value,
    });
  }, [packages?.length, promotedMoePackageCode, totalVoucher?.length]);
  const updateNumberOfElements = () => {
    const PACKAGE_WIDTH = 318;
    const padding = 60;
    if (packagesRef.current) {
      const containerWidth = packagesRef?.current?.offsetWidth - padding;
      if (containerWidth > 0) {
        const number = Math.floor(containerWidth / PACKAGE_WIDTH);
        setNumberOfElements(number);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('resize', updateNumberOfElements);
    updateNumberOfElements();
    return () => {
      window.removeEventListener('resize', updateNumberOfElements);
    };
  }, [updateNumberOfElements, packagesRef?.current, openSidebar]);

  return (
    <StyledPackages
      isVietNam={isVietNam}
      num={currentPackages.length}
      isMobile={isMobile}
      useRenameMergeProduct={useRenameMergeProduct}
    >
      <div className={!isMobile ? 'duration-header' : 'duration-header-mobile'}>
        <div className="title-pricing-model">{t('studioPrcing')}</div>
        <div className="tab-group-wrapper">
          {Object.values(MERGE_PRODUCT_DURATION).map((value) => (
            <div
              className={packageDuration === value ? `tabs-active` : `tabs`}
              onClick={() => handleChangePackageDuration(value)}
              role="button"
              tabIndex={0}
              data-id="select-pricing-duration"
              data-pricing-duration={value}
            >
              {t(PACKAGE_DURATION_LABEL[value])}
              {value !== MERGE_PRODUCT_DURATION.MONTHLY && !!saveUpToPercent() && (
                <div className="save">
                  {t(isMobile ? 'saveUpToMobile' : 'saveUpTo', {
                    percent: saveUpToPercent(),
                  }).toUpperCase()}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Pricing banner */}
      {checkShowBannerVoucherNew() &&
        voucherBannerToShow.length > 0 &&
        !isMobile && (
          <div className="packages-wrapper">
            <SwiperVoucherBanner bannerVoucher={voucherBannerToShow} />
          </div>
        )}

      {/* List packages */}
      <div ref={packagesRef} className="packages-wrapper">
        <ProcessHandler loading={loading} mt="30px" mb="30px" align="center">
          {renderPackages(currentPackages)}
        </ProcessHandler>
      </div>

      {/* Policy note */}
      <div className="notice-ai-voice-container">
        <span className="notice-ai-voice">{t('noticeAIVoice1')}</span>
        <span className="notice-ai-voice">{t('noticeAIVoice2')}</span>
      </div>
    </StyledPackages>
  );
};

export default NewPricingModelV3;
